import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/layout/Layout";
import { Board } from "@/components/board/Board";
import { MotionLayer } from "@/components/board/MotionLayer";
import { GameHUD } from "@/components/game/GameHUD";
import { ControlBar } from "@/components/game/ControlBar";
import { MoveExplanation } from "@/components/game/MoveExplanation";
import { EventLog } from "@/components/game/EventLog";
import { ResultDialog } from "@/components/game/ResultDialog";
import { TESTIDS } from "@/constants/testIds";
import { getEngine } from "@/game/registry";
import { chooseAIMoveForRuleset, getRuleset } from "@/game/rulesets";
import { useSettings } from "@/hooks/useSettings";
import { loadSavedGame, saveGame, clearSavedGame } from "@/hooks/useSavedGame";
import { recordGame } from "@/hooks/useStats";
import { useAuth, authedHeaders, API } from "@/hooks/useAuth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";

const PLAYER_A = "A";
const PLAYER_B = "B";

export function GamePage({ rulesetId: rulesetIdProp = "zimbabwe-standard" }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const routeParams = useParams();
  const { settings } = useSettings();
  const { user } = useAuth();

  // Route param wins over the prop so /play/:rulesetId works.
  const rulesetId = routeParams.rulesetId || rulesetIdProp;
  const ruleset = getRuleset(rulesetId);
  const engine = useMemo(() => getEngine(rulesetId), [rulesetId]);

  // Mode setup
  const mode = params.get("mode") || "local";
  const difficulty = params.get("difficulty") || "intermediate";
  const humanSide = params.get("side") === "B" ? PLAYER_B : PLAYER_A;
  const aiSide = humanSide === PLAYER_A ? PLAYER_B : PLAYER_A;
  const wantContinue = params.get("continue") === "1";
  const sowingDirection = params.get("dir") === "CW" ? "CW" : "CCW";

  // Initialise state (continue saved game if requested)
  const [state, setState] = useState(() => {
    if (wantContinue) {
      const saved = loadSavedGame();
      if (saved && saved.state && saved.state.rulesetId === rulesetId) {
        return saved.state;
      }
    }
    return engine.createInitialState(rulesetId, { sowingDirection });
  });

  const startedAtRef = useRef(Date.now());
  const [selected, setSelected] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showPassScreen, setShowPassScreen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [recordedRef, setRecordedRef] = useState(false);

  const legalMoves = useMemo(() => engine.getLegalMoves(state), [engine, state]);
  const legalSet = useMemo(
    () => new Set(legalMoves.map(([r, c]) => `${r},${c}`)),
    [legalMoves]
  );

  // Reset state when rulesetId changes (navigating between variants).
  useEffect(() => {
    setState(engine.createInitialState(rulesetId, { sowingDirection }));
    setSelected(null);
    setPendingConfirm(null);
    setResultOpen(false);
    setRecordedRef(false);
    startedAtRef.current = Date.now();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesetId]);

  // Auto-save active games
  useEffect(() => {
    if (state.status === "ACTIVE") {
      saveGame({ state, mode, difficulty, rulesetId, side: humanSide });
    } else {
      clearSavedGame();
    }
  }, [state, mode, difficulty, rulesetId, humanSide]);

  // Record stats once when game ends
  useEffect(() => {
    if (state.status !== "ACTIVE" && !recordedRef) {
      const moves = state.moveNumber;
      const captures = (state.captures.A || 0) + (state.captures.B || 0);
      const largestCapture =
        state.history?.reduce((m, h) => Math.max(m, h.captured || 0), 0) || 0;
      const durationMs = Date.now() - startedAtRef.current;

      let outcome = "draw";
      if (state.winner) {
        if (mode === "single") {
          outcome = state.winner === humanSide ? "win" : "loss";
        } else {
          outcome = state.winner === PLAYER_A ? "win" : "loss";
        }
      }
      recordGame({
        rulesetId,
        difficulty: mode === "single" ? difficulty : null,
        outcome,
        moves,
        captures,
        largestCapture,
        durationMs,
      });
      if (user) {
        axios
          .post(
            `${API}/games/result`,
            {
              ruleset_id: rulesetId,
              mode,
              difficulty: mode === "single" ? difficulty : null,
              outcome,
              moves,
              captures,
              largest_capture: largestCapture,
              duration_ms: durationMs,
            },
            { headers: authedHeaders() }
          )
          .catch(() => {});
      }
      setRecordedRef(true);
      setResultOpen(true);
    }
  }, [state.status, recordedRef, mode, difficulty, humanSide, rulesetId, state, user]);

  // AI move loop
  useEffect(() => {
    if (mode !== "single") return;
    if (state.status !== "ACTIVE") return;
    if (state.currentPlayer !== aiSide) return;
    if (animating) return;

    const delay = difficulty === "advanced" ? 200 : 350;
    const t = setTimeout(() => {
      const m = chooseAIMoveForRuleset(state, difficulty, rulesetId);
      if (!m) return;
      executeMove(m[0], m[1]);
    }, delay);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, mode, difficulty, aiSide, animating, rulesetId]);

  // Local pass-device screen between human turns
  useEffect(() => {
    if (mode !== "local") return;
    if (!settings.passDeviceScreen) return;
    if (state.status !== "ACTIVE") return;
    if (state.moveNumber === 0) return;
    setShowPassScreen(true);
  }, [state.moveNumber, mode, settings.passDeviceScreen, state.status]);

  const executeMove = useCallback(
    (r, c) => {
      try {
        let { state: next } = engine.applyMove(state, r, c);
        // Nchombwa: if a pendingCapture was emitted, auto-pick the largest
        // opponent pits as the "free" extra captures. (UI picker pending.)
        if (next.pendingCapture && engine.completeAdditionalCapture) {
          const { additional, pool } = next.pendingCapture;
          // Sort pool by board value descending and pick top `additional`.
          const sortedPool = [...pool]
            .map(([rr, cc]) => ({ pit: [rr, cc], n: next.board[rr][cc] }))
            .filter((x) => x.n > 0)
            .sort((a, b) => b.n - a.n);
          const picks = sortedPool.slice(0, additional).map((x) => x.pit);
          if (picks.length === additional) {
            ({ state: next } = { state: engine.completeAdditionalCapture(next, picks) });
          }
        }
        setAnimating(true);
        const animMs = settings.reducedMotion ? 0 : Math.max(80, 220 / (settings.animationSpeed || 1));
        setTimeout(() => {
          setState(next);
          setSelected(null);
          setPendingConfirm(null);
          setAnimating(false);
        }, animMs);
      } catch (e) {
        toast.error("That move isn't legal here.");
      }
    },
    [engine, state, settings.animationSpeed, settings.reducedMotion]
  );

  const onPitSelect = (r, c) => {
    if (animating) return;
    if (state.status !== "ACTIVE") return;
    if (mode === "single" && state.currentPlayer === aiSide) return;
    const key = `${r},${c}`;
    if (!legalSet.has(key)) {
      setSelected(null);
      setPendingConfirm(null);
      return;
    }
    if (settings.confirmMove) {
      if (pendingConfirm && pendingConfirm[0] === r && pendingConfirm[1] === c) {
        executeMove(r, c);
        return;
      }
      setPendingConfirm([r, c]);
      setSelected([r, c]);
      return;
    }
    executeMove(r, c);
  };

  const onUndo = () => {
    if (state.history.length === 0) return;
    let s = engine.undoMove(state);
    if (mode === "single" && s.history.length > 0 && s.currentPlayer === aiSide) {
      s = engine.undoMove(s);
    }
    setState(s);
    setSelected(null);
    setPendingConfirm(null);
    setResultOpen(false);
    setRecordedRef(false);
  };

  const onRestart = () => {
    setState(engine.createInitialState(rulesetId, { sowingDirection }));
    setSelected(null);
    setPendingConfirm(null);
    setShowLog(false);
    setResultOpen(false);
    setRecordedRef(false);
    startedAtRef.current = Date.now();
    clearSavedGame();
  };

  const onHome = () => navigate("/");

  const is4Row = (state.board?.length || 4) === 4;
  // Direction banner is meaningful for Zimbabwe (chosen at game start) and
  // suppressed elsewhere because each engine has its own direction policy.
  const showDirectionBanner = rulesetId === "zimbabwe-standard";

  return (
    <Layout hideFooter>
      <section
        data-testid={TESTIDS.gamePage}
        className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6"
      >
        <div className="lg:col-span-8 space-y-4">
          <GameHUD
            state={state}
            rulesetName={ruleset?.name || "Tsoro"}
            mode={mode}
            humanSide={humanSide}
          />

          <Board
            board={state.board}
            currentPlayer={state.currentPlayer}
            legalSet={mode === "single" && state.currentPlayer === aiSide ? new Set() : legalSet}
            selected={selected}
            disabled={animating || state.status !== "ACTIVE"}
            showHints={settings.hints}
            coords={settings.coordLabels}
            highContrast={settings.highContrast}
            sowingDirection={state.sowingDirection || "CCW"}
            showDirectionBanner={showDirectionBanner}
            onPitSelect={onPitSelect}
          />
          <MotionLayer
            active={!animating && state.status === "ACTIVE" && !(mode === "single" && state.currentPlayer === aiSide)}
            legalSet={legalSet}
            onPlay={(r, c) => executeMove(r, c)}
          />

          {settings.confirmMove && pendingConfirm && (
            <div className="flex flex-wrap gap-2 items-center">
              <p className="text-sm text-muted-foreground">
                Confirm move from pit ({pendingConfirm[0]}, {pendingConfirm[1] + 1})?
              </p>
              <Button
                data-testid={TESTIDS.confirmMoveBtn}
                onClick={() => executeMove(pendingConfirm[0], pendingConfirm[1])}
                className="rounded-full"
              >
                Confirm
              </Button>
              <Button
                data-testid={TESTIDS.cancelMoveBtn}
                variant="outline"
                onClick={() => {
                  setPendingConfirm(null);
                  setSelected(null);
                }}
                className="rounded-full"
              >
                Cancel
              </Button>
            </div>
          )}

          <ControlBar
            onUndo={onUndo}
            onRestart={onRestart}
            onHome={onHome}
            onToggleLog={() => setShowLog((v) => !v)}
            canUndo={state.history.length > 0}
            disabled={animating}
          />
        </div>

        <aside className="lg:col-span-4 space-y-4">
          {settings.moveExplanations && <MoveExplanation state={state} rulesetId={rulesetId} />}
          {showLog && <EventLog state={state} />}
          {!showLog && (
            <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm space-y-2">
              <p className="font-semibold text-foreground">Quick tips · {ruleset?.name || "Tsoro"}</p>
              <ul className="list-disc pl-5 text-muted-foreground space-y-1">
                {(ruleset?.keyRules || []).map((rule, i) => (
                  <li key={i}>{rule}</li>
                ))}
                {!is4Row && <li>This variant uses a single sowing loop across both rows.</li>}
              </ul>
            </div>
          )}
        </aside>
      </section>

      <ResultDialog
        open={resultOpen}
        state={state}
        rulesetName={ruleset?.name || "Tsoro"}
        mode={mode}
        humanSide={humanSide}
        onRematch={onRestart}
        onReview={() => {
          setResultOpen(false);
          setShowLog(true);
        }}
        onHome={onHome}
        durationMs={Date.now() - startedAtRef.current}
      />

      {showPassScreen && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Pass device</p>
            <p className="font-display text-4xl mt-2">
              {state.currentPlayer === "A" ? "Player 1" : "Player 2"}, your turn
            </p>
            <p className="mt-4 text-muted-foreground">
              Hand the device to the next player, then tap below to reveal the board.
            </p>
            <Button
              size="lg"
              onClick={() => setShowPassScreen(false)}
              className="mt-6 rounded-full px-7"
            >
              <Eye className="h-5 w-5 mr-2" /> I&apos;m ready
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
